import { select } from '@ps/web-core-store';
import { useBodyScrollLock } from '@ps/web-shared-components';
import classNames from 'clsx';
import { clamp, throttle } from 'lodash';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './Slider.scss';

export interface Props {
  children?: React.ReactNode;
  className?: string;
  innerClassName?: string;
  innerRef?: React.RefObject<HTMLDivElement>;
  onScroll?(state: State): void;
  style?: React.CSSProperties;
}

interface InnerProps {
  children?: React.ReactNode;
  innerClassName?: string;
  style?: React.CSSProperties;
  sliderRef: React.RefObject<HTMLDivElement>;
  scroll(direction?: Direction): void;
  reset(): void;
}

export interface State {
  left: boolean;
  right: boolean;
}

export enum Direction {
  LEFT = -1,
  RIGHT = 1
}

export interface SliderHandles {
  scroll(direction?: Direction): void;
  reset(): void;
}

const InnerSlider = forwardRef<HTMLDivElement, InnerProps>(
  ({ sliderRef, scroll, innerClassName, style, children }, ref) => {
    const isModalOpen = useSelector(select.modal.isModalOpen);
    useBodyScrollLock(isModalOpen, sliderRef?.current);
    useImperativeHandle(ref, () => sliderRef.current as HTMLDivElement);

    return (
      <div className={styles.inner} ref={sliderRef} onScroll={() => scroll()}>
        <div className={innerClassName} style={style}>
          {children}
        </div>
      </div>
    );
  }
);

const Slider = forwardRef<SliderHandles, Props>((props, ref) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [slide, setSlide] = useState({ left: false, right: false });

  const scroll = throttle((direction?: Direction) => {
    const { current: sliderNode } = sliderRef;
    let last;

    if (!sliderNode || !sliderNode.firstElementChild) {
      return;
    }

    const { children } = sliderNode.firstElementChild;

    for (let i = children.length - 1; i >= 0; i--) {
      if (['static', 'relative'].includes(getComputedStyle(children[i]).position as string)) {
        last = children[i] as HTMLElement;
        break;
      }
    }

    const maxRight =
      (last && last.offsetLeft + Math.floor(last.getBoundingClientRect().width) - 1) ||
      sliderNode.scrollWidth - sliderNode.scrollLeft;
    if (direction && children.length > 1) {
      let left = 0;
      const spacing = (children[0] as HTMLElement).offsetLeft;
      const { scrollLeft, clientWidth: sliderNodeWidth } = sliderNode;

      if (direction === Direction.RIGHT) {
        for (const child of children) {
          const { offsetLeft, clientWidth } = child as HTMLElement;
          if (offsetLeft + clientWidth > scrollLeft + sliderNodeWidth) {
            left = offsetLeft - spacing;
            break;
          }
        }
      } else if (direction === Direction.LEFT) {
        const reverseChildren = Array.from(children).reverse();
        for (const child of reverseChildren) {
          const { offsetLeft, clientWidth } = child as HTMLElement;
          if (offsetLeft < scrollLeft) {
            left = offsetLeft + clientWidth + spacing - sliderNodeWidth;
            break;
          }
        }
      }
      left = clamp(left, 0, sliderNode.scrollWidth);
      sliderNode.scrollTo({ left, behavior: 'smooth' });
    }

    const state = {
      left: sliderNode.scrollLeft > 0,
      right: maxRight > sliderNode.scrollLeft + sliderNode.clientWidth
    };

    if (state.left !== slide.left || state.right !== slide.right) {
      if (props.onScroll) {
        props.onScroll(state);
      }
      setSlide(state);
    }
  }, 100);

  const reset = throttle(() => {
    const { current: sliderNode } = sliderRef;
    sliderNode?.scrollTo({ left: 0, behavior: 'smooth' });
  }, 100);

  useImperativeHandle(ref, () => ({ reset, scroll }));

  useEffect(() => {
    scroll();
    window.addEventListener('resize', () => scroll());
    return () => window.removeEventListener('resize', () => scroll());
  });

  return (
    <div className={classNames(styles.container, props.className)}>
      <InnerSlider
        innerClassName={props.innerClassName}
        style={props.style}
        sliderRef={sliderRef}
        scroll={scroll}
        reset={reset}
        ref={props.innerRef}
      >
        {props.children}
      </InnerSlider>
    </div>
  );
});

export default Slider;
